import { Link } from 'react-router-dom';
import { Phone, ShoppingCart, MapPin, ArrowLeft } from 'lucide-react';
import CITIES, { getCityUrl } from '@/data/cities';
import { useEffect } from 'react';

const LOGO_URL = "/images/cave-city-pawn-logo.png";

const stateNames = { FL: 'Florida', AL: 'Alabama', GA: 'Georgia' };

export default function LocationsIndex() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Pawn Shop Service Areas | Cave City Pawn Shop Serves FL, AL & GA';
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Cave City Pawn Shop serves communities across Florida, Alabama, and Georgia within 50 miles of Marianna, FL. Find your city to learn about our pawn, buy, and sell services near you.');
    setMeta('keywords', 'pawn shop near me, pawn shop Florida, pawn shop Alabama, pawn shop Georgia, Cave City Pawn locations, pawn shop Marianna FL, pawn shop Dothan AL, pawn shop Panama City FL');
    return () => { document.title = 'Cave City Pawn Shop | Buy, Sell & Pawn Guns, Electronics, Jewelry & More'; };
  }, []);

  const grouped = {};
  CITIES.forEach(c => {
    const state = stateNames[c.state] || c.state;
    if (!grouped[state]) grouped[state] = [];
    grouped[state].push(c);
  });
  Object.keys(grouped).forEach(s => grouped[s].sort((a, b) => a.distance - b.distance));

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Nav */}
      <nav className="bg-dirt-brown/95 backdrop-blur-sm shadow-deep py-2 sticky top-0 z-50">
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-900 via-amber-700 to-amber-900 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="Cave City Pawn Shop" className="h-10" width="auto" height={40} />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer"
              className="cave-btn px-4 py-2 bg-magma-red text-white font-heading text-xs sm:text-sm uppercase tracking-wider rounded-rock shadow-deep flex items-center gap-1.5">
              <ShoppingCart size={14} /> Shop Online
            </a>
            <a href="tel:850-615-6105"
              className="cave-btn px-3 py-2 border-2 border-accent text-accent font-heading text-xs sm:text-sm uppercase rounded-rock flex items-center gap-1.5 hover:bg-accent hover:text-dirt-brown transition-colors">
              <Phone size={14} /> <span className="hidden sm:inline">Call Now</span><span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="py-14 md:py-20 cave-gradient noise-overlay relative">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <nav aria-label="Breadcrumb" className="mb-6 flex justify-center">
              <ol className="flex items-center gap-2 text-sm font-body text-stone-light/70">
                <li><Link to="/" className="hover:text-accent transition-colors">Home</Link></li>
                <li className="text-stone-light/50">/</li>
                <li className="text-accent">Service Areas</li>
              </ol>
            </nav>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading text-bone-white uppercase mb-5" data-testid="locations-h1">
              Areas We <span className="text-accent">Serve</span>
            </h1>
            <p className="text-base md:text-lg text-stone-light font-body max-w-2xl mx-auto mb-6 leading-relaxed">
              Cave City Pawn Shop proudly serves communities across <strong className="text-accent">Florida, Alabama, and Georgia</strong> within a 50-mile radius of our Marianna location. Find your city below!
            </p>
            <div className="flex items-center justify-center gap-2 text-stone-light font-body text-sm">
              <MapPin size={16} className="text-accent" />
              <span>4417 Jackson Street, Marianna, FL 32448</span>
              <span className="text-stone-dark">|</span>
              <a href="tel:850-615-6105" className="text-accent font-bold hover:text-white transition-colors">850-615-6105</a>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 60L80 50C160 40 320 20 480 14C640 8 800 16 960 22C1120 28 1280 32 1360 34L1440 36V60H0Z" fill="#F4EBD9"/>
            </svg>
          </div>
        </section>

        {/* City Grid by State */}
        <section className="py-14 md:py-20" data-testid="locations-grid">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {Object.entries(grouped).map(([state, cities]) => (
              <div key={state} className="mb-14 last:mb-0">
                <h2 className="text-2xl md:text-3xl font-heading text-dirt-brown uppercase mb-6 chisel-heading">
                  {state} <span className="text-magma-red">Cities</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                  {cities.map(c => (
                    <Link key={c.slug} to={getCityUrl(c)} data-testid={`loc-link-${c.slug}`}
                      className="stone-card bg-card border-2 border-dirt-brown/10 rounded-rock-lg p-4 text-center hover:border-magma-red/30 transition-all group">
                      <p className="font-heading text-sm text-dirt-brown uppercase group-hover:text-magma-red transition-colors leading-tight">{c.name}</p>
                      <p className="text-xs text-muted-foreground font-body mt-1">{c.distance === 0 ? 'Our Home!' : `${c.distance} miles ${c.direction}`}</p>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 bg-dirt-brown">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-heading text-bone-white uppercase mb-4">Do Not See Your City?</h2>
            <p className="text-base text-stone-light font-body mb-6">We serve all communities in the Florida Panhandle, Southeast Alabama, and Southwest Georgia. Give us a call!</p>
            <a href="tel:850-615-6105" className="cave-btn inline-flex items-center gap-2 px-8 py-4 bg-accent text-dirt-brown font-heading text-lg uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
              <Phone size={22} /> Call 850-615-6105
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-foreground text-bone-white py-8">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-dirt-brown via-stone-dark to-dirt-brown" />
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-stone-dark font-body">&copy; {new Date().getFullYear()} Cave City Pawn Shop &amp; Trading Company. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/" className="text-sm text-stone-light font-body hover:text-accent">Home</Link>
            <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer" className="text-sm text-stone-light font-body hover:text-accent">Shop Online</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
